import { IWorkItemOptions, IWorkItemStatusOption } from "models/work-items";
import { useEffect, useState } from "react";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import IWorkItemPermissions from "models/work-items/IWorkItemPermissions";

export type GetOptionsForStatusSignature = (permissions: IWorkItemPermissions, statusId: number, allowComplete?: boolean) => IWorkItemStatusOption[];

type UseWorkItemDraftOptionsResult = [
    IWorkItemOptions | null,
    boolean,
    GetOptionsForStatusSignature
];

type UseWorkItemDraftOptionsSignature = (workspaceId?: number) => UseWorkItemDraftOptionsResult;

const useWorkItemDraftOptions: UseWorkItemDraftOptionsSignature = (workspaceId) => {
    const [ result, setResult ] = useState<IWorkItemOptions | null>(null);
    const [ loading, setLoading ] = useState<boolean>(false);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        if (!user || !user.access_token)
            return;
        if (workspaceId && workspaceId == 0)
            return;
        const controller = new AbortController();
        
        (async () => {
            setLoading(true);
            try {
                const url = workspaceId
                    ? `/api/v3/drafts/workitems/options/${workspaceId}`
                    : `/api/v3/drafts/workitems/options`;
                const resp = await fetcher(url, {
                    signal: controller.signal
                }, async (resp, err) => {
                    if (resp.status === 404) {
                        return true;
                    }
                    return false;
                });

                if (!resp.ok) {
                    switch(resp.status) {
                        case 404:
                            return;
                    }
                }

                const data = await resp.json() as IWorkItemOptions;
                setResult(data);
            } catch (err) {

            } finally {
                if (!controller.signal.aborted) 
                    setLoading(false);
            }
        })();
    }, [ workspaceId, user ]);

    const getOptionsForStatus: GetOptionsForStatusSignature = (permissions, statusId, allowComplete) => {
        if (!result)
            return [];

        allowComplete = allowComplete ?? true;

        const currentStatus = result.statuses.find(status => status.id === statusId);
        if (!currentStatus)
            return [];

        let statusOptions = currentStatus.options.map(opt => ({
            id: opt.id,
            name: opt.name,
            value: opt.value
        }));
        statusOptions.push({
            id: currentStatus.id,
            name: currentStatus.name,
            value: currentStatus.value
        });

        return statusOptions.sort((a,b) => a.name.localeCompare(b.name));
    };

    return [
        result,
        loading,
        getOptionsForStatus
    ]
};

export default useWorkItemDraftOptions;